import {StateField, StateEffect, RangeSet, RangeSetBuilder} from '@codemirror/state'
import {EditorView, Decoration, type DecorationSet, GutterMarker, gutterLineClass} from '@codemirror/view'

interface SelectionAttachmentRange {
  from: number
  to: number
}

export const addSelectionAttachmentMark = StateEffect.define<SelectionAttachmentRange>({
  map: ({from, to}, change) => ({from: change.mapPos(from), to: change.mapPos(to)}),
})

export const addSelectionAttachmentGutters = StateEffect.define<SelectionAttachmentRange>({
  map: ({from, to}, change) => ({from: change.mapPos(from), to: change.mapPos(to)}),
})

export const addSelectionAttachmentLines = StateEffect.define<SelectionAttachmentRange>({
  map: ({from, to}, change) => ({from: change.mapPos(from), to: change.mapPos(to)}),
})

export const clearSelectionAttachments = StateEffect.define<void>()

const selectionAttachmentMark = Decoration.mark({class: 'cm-selectionAttachment-mark'})

/** Create a gutter marker for highlighted line numbers */
const selectionAttachmentGutterMarker = new (class extends GutterMarker {
  override elementClass = 'cm-selectionAttachment-gutter'
})()

const selectionAttachmentLineDeco = Decoration.line({class: 'cm-selectionAttachment-line'})

export const selectionAttachmentMarkField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },
  update(marks, tr) {
    marks = marks.map(tr.changes)
    for (const e of tr.effects) {
      if (e.is(addSelectionAttachmentMark)) {
        if (e.value.from !== e.value.to) {
          const b = new RangeSetBuilder<Decoration>()
          b.add(e.value.from, e.value.to, selectionAttachmentMark)
          // Replace all existing mark decorations with new ones
          marks = b.finish()
        } else {
          // Clear mark decorations if the range is empty
          marks = Decoration.none
        }
      } else if (e.is(clearSelectionAttachments)) {
        marks = Decoration.none
      }
    }
    return marks
  },
  provide: f => EditorView.decorations.from(f),
})

export const selectionAttachmentGutterField = StateField.define<RangeSet<GutterMarker>>({
  create() {
    return RangeSet.empty
  },
  update(gutters, tr) {
    gutters = gutters.map(tr.changes)
    for (const e of tr.effects) {
      if (e.is(addSelectionAttachmentGutters)) {
        if (e.value.from !== e.value.to) {
          const {state} = tr
          const startLine = state.doc.lineAt(e.value.from)
          const endLine = state.doc.lineAt(e.value.to)

          const b = new RangeSetBuilder<GutterMarker>()

          // Add gutter markers for each line that contains the selection
          for (let lineNumber = startLine.number; lineNumber <= endLine.number; lineNumber++) {
            const line = state.doc.line(lineNumber)
            b.add(line.from, line.from, selectionAttachmentGutterMarker)
          }

          gutters = b.finish()
        }
      } else if (e.is(clearSelectionAttachments)) {
        gutters = RangeSet.empty
      }
    }
    return gutters
  },
  provide: f => gutterLineClass.from(f),
})

export const selectionAttachmentLineField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },
  update(lines, tr) {
    lines = lines.map(tr.changes)
    for (const e of tr.effects) {
      if (e.is(addSelectionAttachmentLines)) {
        if (e.value.from !== e.value.to) {
          const {state} = tr
          const startLine = state.doc.lineAt(e.value.from)
          const endLine = state.doc.lineAt(e.value.to)

          const b = new RangeSetBuilder<Decoration>()

          // Add line decorations for each line that contains the selection
          for (let lineNumber = startLine.number; lineNumber <= endLine.number; lineNumber++) {
            const line = state.doc.line(lineNumber)
            b.add(line.from, line.from, selectionAttachmentLineDeco)
          }

          // Replace all existing line decorations with new ones
          lines = b.finish()
        } else {
          // Clear line decorations if the range is empty
          lines = Decoration.none
        }
      } else if (e.is(clearSelectionAttachments)) {
        lines = Decoration.none
      }
    }
    return lines
  },
  provide: f => EditorView.decorations.from(f),
})
